import type {
  AccessContext,
  IEventBus,
  IHttpClient,
  ILayoutService,
  ILogger,
  ITelemetry,
  IUiService,
  ModuleManifest,
  RuntimeConfig,
  Session,
  SharedState,
} from "@crialume/sdk";
import type { AuthAdapter } from "./auth/auth-adapter";
import { AuthService } from "./auth/auth-service";
import { createLocalTokenStorage, type TokenStorage } from "./auth/token-storage";
import { createScopedBus, EventBus } from "./events/event-bus";
import { FetchHttpClient } from "./http/fetch-http-client";
import { createLogger, noopTelemetry, type LogLevel } from "./observability/logger";
import {
  createLocalRealtimeClient,
  createScopedRealtime,
  type ManagedRealtimeClient,
} from "./realtime/realtime-emitter";
import { ModuleRegistry, type RegisterResult } from "./registry/module-registry";
import { createStore, type WritableStore } from "./store/create-store";

export interface KernelOptions<C extends RuntimeConfig = RuntimeConfig> {
  config: C;
  authAdapter: AuthAdapter;
  manifests: readonly ModuleManifest[];
  initialState: SharedState;
  /** Implementados pelo shell — o core não conhece o design system. */
  ui: IUiService;
  layout: ILayoutService;
  telemetry?: ITelemetry;
  tokenStorage?: TokenStorage;
  logLevel?: LogLevel;
  /** Prefixo das rotas de módulo. Padrão: "/app". */
  modulesBasePath?: string;
  /**
   * Cria o cliente de tempo real (ex.: `createSignalRRealtimeClient`). O kernel
   * abre a conexão com sessão e fecha no logout. Padrão: cliente local sem rede.
   */
  createRealtime?: (deps: {
    getAccessToken: () => string | null;
    logger: ILogger;
  }) => ManagedRealtimeClient;
}

export interface Kernel<C extends RuntimeConfig = RuntimeConfig> {
  readonly config: C;
  readonly bus: IEventBus;
  readonly http: IHttpClient;
  /** Para os módulos é `IAuthService`; o shell usa também `initialize`/`changePassword`. */
  readonly auth: AuthService;
  readonly store: WritableStore<SharedState>;
  readonly registry: ModuleRegistry;
  /** Conexão de tempo real (aberta/fechada pelo kernel conforme a sessão). */
  readonly realtime: ManagedRealtimeClient;
  readonly ui: IUiService;
  readonly layout: ILayoutService;
  readonly telemetry: ITelemetry;
  readonly logger: ILogger;
  readonly registration: RegisterResult;
  readonly modulesBasePath: string;
  /** Snapshot para regras de acesso (sessão atual + flags). */
  getAccessContext(): AccessContext;
  /**
   * Recupera a sessão antes do primeiro render (refresh silencioso pelo
   * cookie). Sem isso, a rota protegida mandaria para o login quem já está logado.
   */
  initialize(): Promise<void>;
  destroy(): void;
}

/**
 * Composition root do runtime. Todas as dependências concretas nascem aqui,
 * FORA do React (no CadFrn eram criadas dentro de um `useMemo` do CoreProvider,
 * o que impedia usá-las em testes, loaders ou antes do primeiro render).
 */
export function createKernel<C extends RuntimeConfig>(options: KernelOptions<C>): Kernel<C> {
  const logLevel = options.logLevel ?? "info";
  const logger = createLogger("core", logLevel);
  const telemetry = options.telemetry ?? noopTelemetry;
  const modulesBasePath = options.modulesBasePath ?? "/app";

  const bus = new EventBus(createLogger("bus", logLevel));

  // http precisa do token (auth) e auth precisa do http (login): os callbacks
  // só leem `auth` depois que ele existe, o que quebra o ciclo.
  const http: IHttpClient = new FetchHttpClient({
    baseUrl: options.config.apiBaseUrl,
    getAccessToken: () => auth.getAccessToken(),
    refreshSession: () => (auth.canRefresh ? auth.refresh() : Promise.resolve(false)),
    onUnauthorized: (url) => {
      bus.emit("auth:unauthorized", { url });
      auth.expire();
    },
    // 403 inesperado: a permissão pode ter acabado de mudar; o token novo traz as atuais.
    onForbidden: () => auth.refreshAccess(),
  });
  const auth: AuthService = new AuthService({
    adapter: options.authAdapter,
    storage: options.tokenStorage ?? createLocalTokenStorage(),
    bus,
    getHttp: () => http,
  });

  const store = createStore<SharedState>(options.initialState);

  const realtimeLogger = createLogger("realtime", logLevel);
  const realtime =
    options.createRealtime?.({
      getAccessToken: () => auth.getAccessToken(),
      logger: realtimeLogger,
    }) ?? createLocalRealtimeClient({ logger: realtimeLogger });

  // Conexão por usuário: abre no login (e no boot com sessão), fecha no logout
  // e reabre ao trocar de conta ou de permissões (o servidor monta os grupos da
  // conexão a partir do token, ex.: quem recebe o QR Code). Renovação de token
  // com as mesmas permissões não reconecta: o servidor fecha a conexão quando o
  // token expira e o cliente volta com o novo.
  let realtimeKey: string | null = null;
  const syncRealtime = (session: Session | null) => {
    // Senha provisória: o backend só libera a troca de senha.
    const key =
      session && !session.mustChangePassword
        ? `${session.user.id}|${[...session.permissions].sort().join(",")}`
        : null;
    if (key === realtimeKey) return;
    const previous = realtimeKey;
    realtimeKey = key;
    if (!key) {
      void realtime.stop();
    } else if (previous) {
      void realtime.stop().then(() => {
        if (realtimeKey === key) void realtime.start();
      });
    } else {
      void realtime.start();
    }
  };
  let stopRealtimeSync: (() => void) | null = null;

  const registry = new ModuleRegistry({
    logger,
    telemetry,
    createContext: (manifest) => {
      const scoped = createScopedBus(bus);
      const scopedRealtime = createScopedRealtime(realtime);
      return {
        context: Object.freeze({
          moduleId: manifest.id,
          basePath: `${modulesBasePath}/${manifest.id}`,
          bus: scoped.bus,
          http,
          auth,
          ui: options.ui,
          layout: options.layout,
          store: store.readonly,
          config: options.config,
          logger: createLogger(`module:${manifest.id}`, logLevel),
          telemetry,
          realtime: scopedRealtime.realtime,
        }),
        dispose: () => {
          scoped.dispose();
          scopedRealtime.dispose();
        },
      };
    },
  });

  registry.subscribe((event) => {
    if (event.type === "loaded") {
      bus.emit("module:loaded", { moduleId: event.moduleId, loadTimeMs: event.loadTimeMs });
    }
    if (event.type === "error") {
      bus.emit("module:error", { moduleId: event.moduleId, message: event.error.message });
    }
  });

  const registration = registry.register(options.manifests);

  return {
    config: options.config,
    bus,
    http,
    auth,
    store,
    registry,
    realtime,
    ui: options.ui,
    layout: options.layout,
    telemetry,
    logger,
    registration,
    modulesBasePath,
    getAccessContext: () => ({ session: auth.getSession(), config: options.config }),
    initialize: async () => {
      await auth.initialize();
      // Só depois do boot: antes disso a sessão ainda pode estar sendo recuperada.
      stopRealtimeSync ??= auth.subscribe(syncRealtime);
      syncRealtime(auth.getSession());
    },
    destroy: () => {
      stopRealtimeSync?.();
      void realtime.stop();
      auth.destroy();
    },
  };
}
